import React, {Fragment, useCallback, useEffect, useState} from "react";
import EADialog from "../../EADialog";
import EAButtonBar from "../../EAButtonBar";
import EAButton from "../../EAButton";
import usePathParam from "../../../utils/usePathParam";
import {NUMBER} from "../../../utils/URLUtils";
import {ANLAGE, BESCHEID, BESCHEID_MESSSTELLE, BETRIEB, MESSSTELLE} from "../../../consts/fachobjekt";
import EATree from "../../tree/EATree";
import {EATreeItem} from "../../tree/EATreeItem";
import EACheckbox from "../../EACheckbox";
import {buildBescheidTitle} from "../../../initialObjects/BescheidObjects";
import EADownloadButton from "../../EADownloadButton";
import {postDruckausgabe} from "../../../services/BetriebeService";
import {useHttp} from "../../../services/HttpService";
import {getAnlageEndpoint} from "../../../services/AnlagenService";

export default function DruckausgabeDialog({open, onClose, navTree, navTreeLoading}) {
    const [fachobjekte, setFachobjekte] = useState({});
    const [expanded, setExpanded] = React.useState([]);
    const [fachobjekteComprimised, setFachobjekteComprimised] = useState([]);
    const [selected, setSelected] = React.useState([]);
    const betriebsId = usePathParam("id", NUMBER);
    const anlageId = usePathParam("anlageid", NUMBER);
    const messstelleId = usePathParam("messstellenid", NUMBER);
    const bescheidsId = usePathParam("bescheidsid", NUMBER);
    const [{data: anlageData, loading: anlageLoading}] = useHttp(getAnlageEndpoint(getAnlageId()));

    function getAnlageId() {
        if (anlageId) {
            return anlageId;
        }
        return 0;
    }

    const buildFachobjekt = useCallback((fachobjekt, fachobjektTyp) => {
        if (fachobjekt) {
            return createFachobjekt(
                fachobjektTyp,
                fachobjekt,
                fachobjekt?.messstellen ? [createUnterfachobjektListObject("Messstellen", fachobjekt.messstellen?.map((m) => buildFachobjekt(m, MESSSTELLE))),] : []
            );
        } else {
            return [];
        }
    }, []);

    const handleToggle = useCallback(
        (event, nodeIds) => setExpanded(nodeIds),
        [setExpanded]
    );

    const isSelected = useCallback(
        (fachobjekt) => selected.some((f) => isSelectedPredicate(fachobjekt, f)),
        [selected]
    );

    const handleSelection = useCallback(
        (checked, fachobjekt) => {
            setSelected((selectedItems) => {
                if (checked) {
                    return [...selectedItems, fachobjekt];
                } else {
                    return selectedItems.filter((f) => !isSelectedPredicate(f, fachobjekt));
                }
            });
        },
        [setSelected]
    );

    const buildBescheidFachobjekt = useCallback((bescheid) => {
        const messstellen = [createUnterfachobjektListObject("Messstellen",
            bescheid.messstellen?.map((a) => buildFachobjekt(a, BESCHEID_MESSSTELLE)))];

        // Sehr hässlicher Workaround, aber da aus Fachobjekten nochmal Fachobjekte geflattet werden, müssen AdditionalInfos
        // eine weitere Ebene tiefer hinzugefügt werden, sodass diese auch ans Backend kommuniziert werden können
        messstellen.forEach(messstelle => messstelle.fachobjekte.forEach(fo => fo.referenceId = bescheid.id));

        if (messstelleId) {
            // Falls zusätzlich zum Bescheid auch eine Messstellen-ID in der URL ist, wird nur diese Messstelle zur Druckauswahl
            // bereitgestellt.
            messstellen[0].fachobjekte = messstellen[0].fachobjekte.filter(messstelle => messstelleId === messstelle.id);
        }

        return createFachobjekt(BESCHEID, {...bescheid, title: buildBescheidTitle(bescheid)}, messstellen);
    }, [messstelleId, buildFachobjekt]);

    const buildAnlageFachobjektHierarchie = useCallback(
        (anlage) => {
            return createFachobjekt(ANLAGE, anlage, [
                createUnterfachobjektListObject(
                    "Misch-/Niederschlagswasser",
                    anlage.entlastungsanlagen?.map((a) => buildFachobjekt(a, ANLAGE))
                ),
                createUnterfachobjektListObject(
                    "Kanalnetze",
                    anlage.kanalnetz?.map((a) => buildFachobjekt(a, ANLAGE))
                ),
                createUnterfachobjektListObject(
                    "Messstellen",
                    anlage.messstellen?.map((a) => buildFachobjekt(a, MESSSTELLE))
                ),
            ]);
        },
        [buildFachobjekt]
    );

    useEffect(() => {
        if (!navTree || !betriebsId || navTreeLoading || anlageLoading) {
            return;
        }
        let newFachobjekte = [];
        if (bescheidsId) {
            if (navTree.bescheide.some((b) => b.id === bescheidsId)) {
                const bescheid = navTree.bescheide.find((b) => b.id === bescheidsId);
                newFachobjekte = createUnterfachobjektListObject("Bescheide", [
                    buildBescheidFachobjekt(bescheid),
                ]);

            } else if (navTree.aenderungsantraege.some((b) => b.id === bescheidsId)) {
                newFachobjekte = createUnterfachobjektListObject("Anträge", [
                    buildBescheidFachobjekt(navTree.aenderungsantraege.find((b) => b.id === bescheidsId)),
                ]);
            }
        } else if (messstelleId) {
            const anlage = findAnlageInHierarchy(navTree, anlageId);
            const innerFachobjektArray = [buildFachobjekt(anlage.messstellen?.find((m) => m.id === messstelleId), MESSSTELLE)];
            newFachobjekte = createUnterfachobjektListObject("Messstellen", innerFachobjektArray);
        } else if (anlageId && anlageData) {
            newFachobjekte = createUnterfachobjektListObject("Anlagen", [
                buildAnlageFachobjektHierarchie(anlageData),
            ]);
        } else if (betriebsId) {
            newFachobjekte = createUnterfachobjektListObject("Betrieb", [
                createFachobjekt(
                    BETRIEB,
                    {
                        id: betriebsId,
                        title: navTree.title,
                    },
                    [
                        createUnterfachobjektListObject("Anlagen", navTree.anlagen.map(buildAnlageFachobjektHierarchie)),
                        createUnterfachobjektListObject("Bescheide", navTree.bescheide.map(buildBescheidFachobjekt)),
                        createUnterfachobjektListObject("Anträge", navTree.aenderungsantraege.map(buildBescheidFachobjekt)),
                    ]
                ),
            ]);
        }
        setFachobjekte(newFachobjekte);
        const tempFachobjekteComprimised = fachobjekteToOneDimensionalArray(newFachobjekte);
        setFachobjekteComprimised(tempFachobjekteComprimised);
        setExpanded([]);
        setSelected(tempFachobjekteComprimised);
    }, [
        navTree,
        betriebsId,
        navTreeLoading,
        anlageId,
        messstelleId,
        bescheidsId,
        buildAnlageFachobjektHierarchie,
        buildBescheidFachobjekt,
        buildFachobjekt,
        anlageData,
        anlageLoading
    ]);

    const buildFachobjektSelectionComponent = useCallback(
        (fachobjekt, title) => {
            const key = getKey(fachobjekt);
            return (
                <EACheckbox
                    id={"druckausgabeSelection-" + key}
                    key={key}
                    checked={isSelected(createCheckboxValue(fachobjekt))}
                    onChange={(e, checked) => {
                        handleSelection(checked, createCheckboxValue(fachobjekt));
                    }}
                >
                    {title}
                </EACheckbox>
            );
        },
        [isSelected, handleSelection]
    );

    const buildFachobjektListComponent = useCallback((fachobjekt, buildFachobjektComp) => {
        const key = getKey(fachobjekt);
        return (
            fachobjekt?.fachobjekte?.length > 0 && (
                <EATreeItem key={key} nodeId={key} label={fachobjekt.title}>
                    {fachobjekt.fachobjekte.map((unterFachobjekt) => buildFachobjektComp(unterFachobjekt, buildFachobjektListComponent))}
                </EATreeItem>
            )
        );
    }, []);

    const buildFachobjektComponent = useCallback(
        (fachobjekt) => {
            const key = getKey(fachobjekt);
            if (fachobjekt.unterFachobjekte) {
                return (
                    <EATreeItem key={key} nodeId={key} label={fachobjekt.title}>
                        {buildFachobjektSelectionComponent(fachobjekt, getFachobjektType(fachobjekt))}
                        {fachobjekt.unterFachobjekte?.map((innerUnterFachobjekt) =>
                            buildFachobjektListComponent(innerUnterFachobjekt, buildFachobjektComponent)
                        )}
                    </EATreeItem>
                );
            }
            return buildFachobjektSelectionComponent(fachobjekt, fachobjekt.title);
        },
        [buildFachobjektSelectionComponent, buildFachobjektListComponent]
    );

    const createDialogContent = useCallback(() => {
        return (
            fachobjekte?.fachobjekte &&
            fachobjekte?.fachobjekte[0] && (
                <Fragment>
                    {fachobjekteComprimised.length > 1 && (
                        <EACheckbox
                            id="selectAllForDruckausgabe"
                            indeterminate={selected.length > 0 && selected.length < fachobjekteComprimised.length}
                            checked={selected.length === fachobjekteComprimised.length}
                            onChange={(event, selectAll) => (selectAll ? setSelected(fachobjekteComprimised) : setSelected([]))}
                        >
                            Alle auswählen
                        </EACheckbox>
                    )}
                    <EATree expanded={expanded} onNodeToggle={handleToggle} style={{minWidth: 800, minHeight: "60vh"}}>
                        {buildFachobjektComponent(fachobjekte.fachobjekte[0])}
                    </EATree>
                </Fragment>
            )
        );
    }, [fachobjekte, fachobjekteComprimised, selected.length, expanded, handleToggle, buildFachobjektComponent]);

    const selectedFachobjekte = useCallback(() => {
        return fachobjekteComprimised.filter((fachobjekt) => isSelected(fachobjekt));
    }, [fachobjekteComprimised, isSelected]);

    const createButtonBar = useCallback(() => {
        return (
            <EAButtonBar>
                <EAButton id="closeDialog" label="Schließen" onClick={() => onClose()}/>
                <EADownloadButton
                    id="auswahlDrucken"
                    color="primary"
                    label="Drucken"
                    disabled={!selected || selected.length === 0}
                    config={postDruckausgabe(betriebsId, selectedFachobjekte())}
                />
            </EAButtonBar>
        );
    }, [betriebsId, selected, onClose, selectedFachobjekte]);

    return (
        <EADialog
            titleChildren="Druckausgabe"
            closeFunction={() => onClose()}
            open={open}
            content={createDialogContent()}
            buttons={createButtonBar()}
            testId="druckausgabeDlg"
        />
    );
}

function getKey(fachobjekt) {
    if (fachobjekt) {
        if (fachobjekt.id) {
            return fachobjekt.title + "-" + fachobjekt.id;
        }
        return fachobjekt.title + ":" + fachobjekt.fachobjekte?.map((f) => f.id).join("-");
    }
    return undefined;
}

function getFachobjektType(fachobjekt) {
    switch (fachobjekt.fachobjekt) {
        case BETRIEB:
            return "Betriebsdaten";
        case ANLAGE:
            return "Anlagendaten";
        case MESSSTELLE:
            return "Messstellendaten";
        default:
            return "Fachobjektdaten";
    }
}

function createUnterfachobjektListObject(title, fachobjekte) {
    return {title, fachobjekte};
}

function createFachobjekt(typ, fachobjekt, unterFachobjekte) {
    if (typ === ANLAGE) {
        return {fachobjekt: typ, id: fachobjekt.id, title: fachobjekt.bezeichnung, unterFachobjekte};
    }
    return {fachobjekt: typ, id: fachobjekt.id, title: fachobjekt.title, unterFachobjekte};
}

function fachobjekteToOneDimensionalArray(fachobjekte) {
    if (fachobjekte?.fachobjekte) {
        return [].concat(...fachobjekte.fachobjekte.map((f) => {
            if (f.unterFachobjekte) {
                return [createCheckboxValue(f)].concat(...f.unterFachobjekte.map((uf) => fachobjekteToOneDimensionalArray(uf)));
            } else {
                return [createCheckboxValue(f)];
            }
        }));
    } else {
        return [];
    }
}

function findAnlageInHierarchy(navTree, anlageId) {
    const direktAnlage = findAnlage(navTree.anlagen, anlageId);
    if (direktAnlage) {
        return direktAnlage;
    }
    const entlastungsanlage = findAnlage([].concat(...navTree.anlagen.map((a) => a.entlastungsanlagen)), anlageId);
    if (entlastungsanlage) {
        return entlastungsanlage;
    }
    const kanalnetz = findAnlage([].concat(...navTree.anlagen.map((a) => a.kanalnetz)), anlageId);
    if (kanalnetz) {
        return kanalnetz;
    }
}

function findAnlage(list, anlageId) {
    return list.find((a) => a?.id === anlageId);
}

function createCheckboxValue(fachobjekt) {
    return {fachobjekt: fachobjekt.fachobjekt, id: fachobjekt.id, title: fachobjekt.title, referenceId: fachobjekt.referenceId};
}

function isSelectedPredicate(fachobjekt, fachobjekt2) {
    return fachobjekt.fachobjekt === fachobjekt2.fachobjekt && fachobjekt.id === fachobjekt2.id;
}
